import calcUnitPrice from 'screens/Invoices/helpers/calcUnitPrice';

export interface LineUnitPrice {
  value: number;
  unit: string;
}

export interface UnitPriceLine {
  /**
   * The rate the user entered, and the unit they entered it against. Absent on
   * lines saved before the invoice API carried them.
   */
  storedValue?: number | null;
  storedUnit?: string | null;
  quantityValue?: number | null;
  quantityUnit?: string | null;
  costValue?: number | null;
}

/**
 * The rate to show for an invoice line, and the unit to show it against.
 *
 * Prefers the stored pair: dividing cost by quantity always yields the invoiced
 * quantity's unit, so $2.00/ton on 2 gallons comes back as $0.01/gal — the same
 * money in a phrasing nobody typed. Lines saved before the rate was persisted
 * fall back to that division.
 *
 * Magnitude only, since a credit line's negative quantity and cost divide out
 * to a positive rate.
 */
const resolveUnitPrice = ({
  storedValue,
  storedUnit,
  quantityValue,
  quantityUnit,
  costValue
}: UnitPriceLine): LineUnitPrice | null => {
  if (
    typeof storedValue === 'number' &&
    Number.isFinite(storedValue) &&
    storedValue !== 0 &&
    storedUnit
  ) {
    return { value: Math.abs(storedValue), unit: storedUnit };
  }

  if (!quantityValue || !costValue) return null;

  const derived = Math.abs(Number(calcUnitPrice(quantityValue, costValue)));
  if (!Number.isFinite(derived)) return null;
  return { value: derived, unit: quantityUnit ?? '' };
};

export default resolveUnitPrice;
